import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql, createFragmentContainer } from 'react-relay';
import get from 'lodash.get';
import { getFilterValues } from '../SbSharedRefineMenu/sbSharedRefineMenuHelpers';
import { trackSearchWithinResults } from '../../utils/tracking/searchBrowse/filterTracking';
import {
    getAppliedFilterValues,
    closeAppliedFilterHandler,
    clearAllAppliedFiltersHandler,
} from '../SbSharedAppliedFilters/sbSharedAppliedFiltersHelpers';
import {
    getValidRespAppliedFilters,
    isBatchAppliedFilter,
} from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap';

// components
import { FormattedMessage } from 'react-intl';
import { SbRespAppliedFiltersItem as SbSharedAppliedFiltersItem } from './SbRespAppliedFiltersItem';
import { useMeasurementUnit } from '../useMeasurementUnit';
import { SbSharedRefineMenuSearchWithin } from '../SbSharedRefineMenu/SbSharedRefineMenuSearchWithin/SbSharedRefineMenuSearchWithin';
import { ExpandingArea } from 'dibs-elements/exports/ExpandingArea';
import { SearchTag } from 'dibs-elements/exports/SearchTag';

// styles
import styles from './SbRespAppliedFilters.scss';

const FILTER_THRESHOLD = 5;

export const SbRespAppliedFiltersComponent = props => {
    const {
        itemSearch,
        currency,
        onCloseClick,
        onClearAllClick,
        viewer,
        user,
        generatedUriRef,
    } = props;
    const { isSbSharedRefineMenuSearchWithinEnabled } = viewer;
    const { appliedFilters, filters, totalResults } = itemSearch;
    const [isViewingMoreFilters, setIsViewingMoreFilters] = useState(false);
    const validAppliedFilters = getValidRespAppliedFilters({ appliedFilters });
    const [measurementUnit] = useMeasurementUnit({ user, itemSearch });

    if (!validAppliedFilters.length) {
        return (
            <div className={styles.container}>
                <div className={styles.headerContainer}>
                    <span className={styles.header}>
                        <FormattedMessage
                            id="sb.SbRespAppliedFilters.noResults"
                            defaultMessage="Filter Results"
                        />
                    </span>
                </div>
                {isSbSharedRefineMenuSearchWithinEnabled && (
                    <SbSharedRefineMenuSearchWithin
                        horizontalSpacing="small"
                        onSubmit={trackSearchWithinResults}
                        itemSearch={itemSearch}
                    />
                )}
            </div>
        );
    }

    const tags = validAppliedFilters.reduce((acc, appliedFilter) => {
        const isBatchApplied = isBatchAppliedFilter(appliedFilter.name);
        const values = getAppliedFilterValues({
            appliedFilter,
            isBatchApplied,
        });

        values.forEach((filterValue, index) => {
            acc.push(
                <SbSharedAppliedFiltersItem
                    key={`${appliedFilter.name}-${index}`}
                    currency={currency}
                    filterName={appliedFilter.name}
                    localizedFilterName={appliedFilter.localizedFilterName}
                    filterValue={isBatchApplied ? null : filterValue}
                    availableValues={getFilterValues(filters, appliedFilter.name)}
                    onCloseClick={() =>
                        onCloseClick({
                            filterName: appliedFilter.name,
                            filterValue,
                            generatedUriRef,
                            isBatchApplied,
                        })
                    }
                    measurementUnit={measurementUnit}
                />
            );
        });
        return acc;
    }, []);
    const numberMoreFilters = Math.max(tags.length - FILTER_THRESHOLD, 0);
    if (numberMoreFilters === 0 && isViewingMoreFilters) {
        setIsViewingMoreFilters(false);
    }
    const extraTags = tags.splice(FILTER_THRESHOLD);

    return (
        <div className={styles.container}>
            <div className={styles.headerContainer}>
                <span className={styles.header}>
                    <FormattedMessage
                        id="sb.SbRespAppliedFilters.title"
                        defaultMessage="Applied Filters"
                    />
                </span>
                <span className={styles.headerResults}>
                    <FormattedMessage
                        id="sb.SbRespAppliedFilters.results"
                        defaultMessage={'{totalResults, plural, one {# result} other {# results}}'}
                        values={{ totalResults }}
                    />
                </span>
            </div>
            <div className={styles.appliedFilters} data-tn="applied-filters-container">
                {tags}
                {!!numberMoreFilters && (
                    <React.Fragment>
                        <ExpandingArea expanded={isViewingMoreFilters}>{extraTags}</ExpandingArea>
                        <div
                            onClick={() => setIsViewingMoreFilters(!isViewingMoreFilters)}
                            className={styles.moreFiltersButtonWrapper}
                        >
                            <SearchTag hasDeleteButton={false} dataTn={'more-filters-button'}>
                                <div className={styles.moreFiltersButton}>
                                    {isViewingMoreFilters ? (
                                        <FormattedMessage
                                            id="sb.SbRespAppliedFilters.viewLess"
                                            defaultMessage="View Less"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="sb.SbRespAppliedFilters.additionalFilters"
                                            defaultMessage="+ {numberMoreFilters} more"
                                            values={{ numberMoreFilters }}
                                        />
                                    )}
                                </div>
                            </SearchTag>
                        </div>
                    </React.Fragment>
                )}
            </div>
            {isSbSharedRefineMenuSearchWithinEnabled && (
                <SbSharedRefineMenuSearchWithin
                    itemSearch={itemSearch}
                    onSubmit={trackSearchWithinResults}
                />
            )}
            <span
                className={styles.clearAllButton}
                data-tn="applied-filters-clear-all"
                onClick={onClearAllClick}
            >
                <FormattedMessage
                    id="sb.SbRespAppliedFilters.clearAll"
                    defaultMessage="Clear All"
                />
            </span>
        </div>
    );
};

SbRespAppliedFiltersComponent.defaultProps = {
    onCloseClick: () => {},
    onClearAllClick: () => {},
};

SbRespAppliedFiltersComponent.propTypes = {
    user: PropTypes.object,
    viewer: PropTypes.object.isRequired,
    currency: PropTypes.string.isRequired,
    itemSearch: PropTypes.object.isRequired,
    // redux
    onCloseClick: PropTypes.func.isRequired,
    onClearAllClick: PropTypes.func.isRequired,
    generatedUriRef: PropTypes.string,
};

function mapDispatchToProps(dispatch, props) {
    return {
        onCloseClick: closeAppliedFilterHandler(dispatch),
        onClearAllClick: clearAllAppliedFiltersHandler(dispatch, props),
    };
}

function mapStateToProps(state) {
    return {
        generatedUriRef: get(state, 'filters.generatedUriRef'),
    };
}

export const SbRespAppliedFilters = createFragmentContainer(
    connect(mapStateToProps, mapDispatchToProps)(SbRespAppliedFiltersComponent),
    {
        viewer: graphql`
            fragment SbRespAppliedFilters_viewer on Viewer {
                isSbSharedRefineMenuSearchWithinEnabled: isEnabledByLocale(
                    featureName: "NON_MVP_SEARCH_BROWSE"
                )
            }
        `,
        itemSearch: graphql`
            fragment SbRespAppliedFilters_itemSearch on ItemSearchQueryConnection {
                ...SbSharedRefineMenuSearchWithin_itemSearch
                ...useMeasurementUnit_itemSearch
                totalResults
                clearAppliedFiltersLinkReference
                filters {
                    name
                    localizedFilterName
                    values {
                        displayName
                        urlLabel
                        linkReference
                    }
                }
                appliedFilters {
                    name
                    localizedFilterName
                    canBeDismissed
                    values {
                        urlLabel
                        linkReference
                        displayName
                        hexCode
                    }
                }
            }
        `,
        user: graphql`
            fragment SbRespAppliedFilters_user on User {
                ...useMeasurementUnit_user
            }
        `,
    }
);
